// campaign_checklists.ts
import { Channel, Platform } from './taskTypes'

// ==== your key sets (unchanged) ====
import {
  metaCampaignKeys,
  simplifiCampaignKeys,
  googleDisplayCampaignKeys,
  googleSearchCampaignKeys,
  seoTier2Keys,
  gtmGa4Keys,
  metaIntegrationKeys,
  linkedinIntegrationKeys,
} from './checklistKeys' // ⬅️ or paste them here
import { collateralKeys, fullBrandKeys, logoKeys, newWebKeys, photoKeys, premiumWebKeys } from './creative_form_config'

// ---- Types returned to the UI ----
export type ChecklistKey = {
  key: string
  label: string
  helperText?: string[]
}
export type ChecklistItem = ChecklistKey & {
  id: string
  done: boolean
  createdTime: string
}
export const ALLOWED_CHECKLIST_TYPES = [
  'campaignChecklist',
  'photography',
  'logoDesign',
  'fullBrandBuild',
  'designMaterials',
  'website',
  'marketingCollateral',
]

// ---- Central resolver ----
export function campaignChecklistFor(ctx: {
  platform: Platform | ''
  channel: Channel | ''
  productTitle?: string | null
}): ChecklistKey[] {
  console.log('CTX', ctx)
  const { platform, channel, productTitle } = ctx

  // Defensive early outs:
  if (!platform && !channel) return []
  if (channel === 'Creative' && platform === 'Other' && productTitle === 'Photo Shoot') return photoKeys
  if (channel === 'Creative' && platform === 'Other' && ['Full Brand Build', 'Branding Guide']?.includes(productTitle))
    return fullBrandKeys
  if (channel === 'Creative' && platform === 'Other' && productTitle === 'Graphic Design - Logo Creation')
    return logoKeys
  if (channel === 'Creative' && platform === 'Other' && ['Premium Website Build']?.includes(productTitle))
    return premiumWebKeys
  if (channel === 'Creative' && platform === 'Other' && ['Website Build']?.includes(productTitle)) return newWebKeys
  if (
    channel === 'Creative' &&
    platform === 'Other' &&
    ['Business Systems Kit', 'Design Materials', 'Marketing Collateral']?.includes(productTitle)
  )
    return collateralKeys
  // Integration flavors (when channel is Integration or platform is a measurement tool)
  if (channel === 'Integration' || platform === 'GTM' || platform === 'GA4') {
    if (platform === 'GTM' || platform === 'GA4') return gtmGa4Keys
    if (platform === 'Meta') return metaIntegrationKeys
    if (platform === 'LinkedIn') return linkedinIntegrationKeys
    // Optional: add more here as you add integrations
    return gtmGa4Keys
  }

  // SEO
  if (channel === 'SEO') return seoTier2Keys

  // Website (no dedicated set? return empty or reuse SEO/site build set)
  if (channel === 'Website') return []

  // Ad platforms
  switch (platform) {
    case 'Meta':
      // Social (your example maps Meta Advertising to channel "Digital", we treat Digital/Social as same family)
      if (channel === 'Social' || channel === 'Digital') return metaCampaignKeys
      return metaCampaignKeys
    case 'Simplifi':
      // Usually Display/Video
      return simplifiCampaignKeys
    case 'Google':
      if (channel === 'Search' || channel === 'Digital') return googleSearchCampaignKeys
      if (channel === 'Display') return googleDisplayCampaignKeys
      // If they route YouTube under Google+Video in your data model:
      if (channel === 'Video') return googleDisplayCampaignKeys
      return []
    case 'YouTube':
      return googleDisplayCampaignKeys
    case 'LinkedIn':
      // No bespoke LI campaign set yet — re-use Meta list or return empty
      return metaCampaignKeys
    default:
      return []
  }
}

// ---- Small helper to materialize -> Row.checklist ----
export function materializeChecklist(keys: ChecklistKey[], nowIso: string): ChecklistItem[] {
  return keys.map((k, i) => ({
    id: `${i}`,
    key: k.key,
    label: k.label,
    helperText: k.helperText,
    done: false,
    createdTime: nowIso,
  }))
}
